import { create } from 'zustand';
import { userService } from '../services/userService';
import { educationService } from '../services/educationService';
import { experienceService } from '../services/experienceService';
import type { User, CreateUserRequest, UserEducation, UserExperience, OrgPathItem } from '../types/api';
import { DateTimeDisplay } from '@t-one-internal/design-system';

const generateId = () => Math.random().toString(36).slice(2, 10);

const unwrapNullableField = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    if (value === 'null' || value === '') return null;
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    if ('Valid' in value && value.Valid === false) return null;
    if ('String' in value) return value.String || null;
    if ('Time' in value) return value.Time || null;
    if ('Bool' in value) return String(value.Bool);
    if ('value' in value) return value.value || null;
  }
  return null;
};

const unwrapDateField = (value: any): string | null => {
  const raw = unwrapNullableField(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toISOString();
};

const normalizeBooleanField = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  if (value && typeof value === 'object') {
    if ('Valid' in value && value.Valid === false) return false;
    if ('Bool' in value) return normalizeBooleanField(value.Bool);
    if ('Int64' in value) return normalizeBooleanField(value.Int64);
  }
  return false;
};

const formatDateOnly = (value: string | null): string | null => {
  if (!value) return null;
  try {
    return DateTimeDisplay.toStandardDate(value);
  } catch {
    return value;
  }
};

const formatDateForAPI = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const mapEducationRecords = (records: UserEducation[] = []) =>
  records.map((record) => ({
    id: record.id ? record.id.toString() : generateId(),
    university: record.school_name ?? null,
    education: record.level ?? null,
    degree: record.field_of_study ?? null,
    year: record.year ? record.year.toString() : null,
  }));

const mapExperienceRecords = (records: UserExperience[] = []) =>
  records.map((record) => {
    const companyName = unwrapNullableField(record.company_name);
    const position = unwrapNullableField(record.position);
    const description = unwrapNullableField(record.description);
    const startDateRaw = unwrapDateField(record.start_date);
    const endDateRaw = unwrapDateField(record.end_date);
    return {
      id: record.id ? record.id.toString() : generateId(),
      organisation: companyName,
      jobTitle: position,
      startDateISO: startDateRaw,
      startDate: formatDateOnly(startDateRaw),
      endDateISO: endDateRaw,
      endDate: formatDateOnly(endDateRaw),
      description: description,
    };
  });

export interface EmployeeDetails {
  id: string;
  user_uuid?: string;
  name: string;
  empId: string;
  email: string;
  phone?: string | null;
  jobTitle: string;
  grade: string;
  manager: string;
  hrPartner: string;
  location: string;
  unit?: string;
  department?: string;
  status?: string;
  avatarColor: string;
  avatarInitial: string;
  isHrManager?: boolean;
  profile_pic_url?: string | null;
  primaryOrgUnitId?: string;
  primaryOrgUnitName?: string;
  org_path?: OrgPathItem[];

  // Personal Information
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  gender?: string | null;
  profilePicture?: string | null;
  employee_id?: string;

  // Education & Experience
  educations?: Array<{
    id: string;
    university: string | null;
    education: string | null;
    degree: string | null;
    year: string | null;
  }>;
  experiences?: Array<{
    id: string;
    organisation: string | null;
    startDate: string | null;
    startDateISO?: string | null;
    jobTitle: string | null;
    endDate: string | null;
    endDateISO?: string | null;
    description?: string | null;
  }>;
  totalYearsOfExperience?: number | null;
  hireDate?: string | null;
  hireDateISO?: string | null;
  startDate?: string | null;
  startDateISO?: string | null;
  positionDate?: string | null;
  positionDateISO?: string | null;

  // Additional job details
  jobTitleId?: string | null;
  positionDescription?: string | null;
  positionDescriptionFileName?: string | null;
  eeocCategory?: string;
  standardHours?: string;
  entity?: string;
  division?: string;
  primaryOrgUnit?: string;
  secondaryManager?: string;
  employmentType?: string;
  race?: string;
  jobCode?: string;
  reportingToTitle?: string;
  workRole?: string;
  trainerContact?: boolean;
  branch?: string;
  phoneExt?: string;
  primary_org_unit_id?: string | number;

  // Benefits & Compensation
  basePay?: number | null;
  bonusAmount?: number | null;
  bonusPercent?: number | null;
  equity?: number | null;
  payRate?: number | null;

  lastUpdated?: Date;
}

interface EmployeeProfileStore {
  selectedEmployee: EmployeeDetails | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadProfile: (userUuid: string) => Promise<void>;
  updateEmployee: (updates: Partial<EmployeeDetails>) => Promise<void>;
  updatePersonalInfo: (updates: Partial<EmployeeDetails>) => Promise<void>;
  refreshEmployeeDetails: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Education CRUD
  addEducation: (education: any) => Promise<void>;
  updateEducation: (educationId: string, updates: any) => Promise<void>;
  removeEducation: (educationId: string) => Promise<void>;

  // Experience CRUD
  addExperience: (experience: any) => Promise<void>;
  updateExperience: (experienceId: string, updates: any) => Promise<void>;
  removeExperience: (experienceId: string) => Promise<void>;
}

export const useEmployeeProfileStore = create<EmployeeProfileStore>((set, get) => ({
  selectedEmployee: null,
  isLoading: false,
  error: null,

  loadProfile: async (userUuid: string) => {
    set({ isLoading: true, error: null });

    try {
      const apiResponse = await userService.getMe();
      const apiUser = apiResponse.user;
      let apiEducations = apiResponse.education ?? [];
      let apiExperiences = apiResponse.experience ?? [];

      // Fallback: fetch education/experience separately if not in main response
      const identifier = apiUser.user_uuid || String(apiUser.id);
      if (!apiEducations.length) {
        try {
          const eduResponse = await educationService.getEducations(identifier);
          apiEducations = eduResponse.education ?? [];
        } catch (e) {
          console.error('Failed to fetch education separately:', e);
        }
      }
      if (!apiExperiences.length) {
        try {
          const expResponse = await experienceService.getExperiences(identifier);
          apiExperiences = expResponse.experience ?? [];
        } catch (e) {
          console.error('Failed to fetch experience separately:', e);
        }
      }

      // Fetch full user details from admin endpoint to get manager/secondaryManager/primaryOrgUnit
      // which may not be returned by the /me endpoint
      let fullUserData: Record<string, unknown> | null = null;
      try {
        const fullResponse = await userService.getUser(identifier);
        fullUserData = (fullResponse.user ?? fullResponse) as unknown as Record<string, unknown>;
      } catch (e) {
        console.error('Failed to fetch full user details:', e);
      }

      // Merge full admin user data into the /me response to fill in missing fields
      // (e.g. manager, secondary_manager, primary_org_unit_id are often absent from /me)
      // meData takes priority, but for fields it returns as null/undefined,
      // we keep the fullUserData value (which has manager, secondary_manager, hr_partner, etc.)
      const meData = apiUser as unknown as Record<string, unknown>;
      const rawUser: Record<string, unknown> = fullUserData
        ? Object.fromEntries(
          Object.entries({ ...fullUserData, ...meData }).map(([key, val]) => {
            // If meData overwrote with null/undefined but fullUserData has a real value, keep fullUserData's value
            if ((val === null || val === undefined) && fullUserData[key] != null) {
              return [key, fullUserData[key]];
            }
            return [key, val];
          })
        )
        : { ...meData };
      console.log('DEBUG: rawUser', rawUser);
      console.log('DEBUG: fullUserData', fullUserData);
      const firstName = unwrapNullableField(rawUser['first_name']) || '';
      const lastName = unwrapNullableField(rawUser['last_name']) || '';
      const name = `${firstName} ${lastName}`.trim() || 'Unknown';
      const emailAddress = unwrapNullableField(rawUser['email']);
      const phoneNumber = unwrapNullableField(rawUser['phone']);
      const gender = unwrapNullableField(rawUser['gender']);
      const profilePicUrl = unwrapNullableField(rawUser['profile_pic_url']);
      const dobISO = unwrapDateField(rawUser['date_of_birth']);
      const hireDateISO = unwrapDateField(rawUser['hire_date']);
      const startDateISO = unwrapDateField(rawUser['start_date']);
      const positionDateISO = unwrapDateField(rawUser['position_date']);
      const standardHours = unwrapNullableField(rawUser['standard_hr_per_week']) ?? unwrapNullableField(rawUser['standard_hours_per_week']);
      const jobTitleApi = unwrapNullableField(rawUser['job_title']);
      const jobTitleIdApi = rawUser['job_title_id'] != null ? String(rawUser['job_title_id']) : null;
      const jobGradeApi = unwrapNullableField(rawUser['job_grade']);
      const eeocCategory = unwrapNullableField(rawUser['eeoc_category']);
      const entity = unwrapNullableField(rawUser['entity'] ?? rawUser['entity_id']);
      const division = unwrapNullableField(rawUser['division'] ?? rawUser['division_id']);
      const locationText = unwrapNullableField(rawUser['location'] ?? rawUser['location_id']);
      const phoneExt = unwrapNullableField(rawUser['phone_ext'] ?? rawUser['phone_extension']);
      const manager = unwrapNullableField(rawUser['manager'] ?? rawUser['manager_id']);
      const secondaryManager = unwrapNullableField(rawUser['secondary_manager'] ?? rawUser['secondary_manager_id']);
      const hrPartner = unwrapNullableField(rawUser['hr_partner'] ?? rawUser['hr_manager_id']);
      const employmentType = unwrapNullableField(rawUser['employment_type']);
      const employmentStatus = unwrapNullableField(rawUser['employment_status']);
      const race = unwrapNullableField(rawUser['race']);
      const jobCode = unwrapNullableField(rawUser['job_code']);
      const employeeId = unwrapNullableField(rawUser['employee_id']);
      const reportingToTitle = unwrapNullableField(rawUser['reporting_to_title']);
      const workRole = unwrapNullableField(rawUser['work_role']);
      const positionDescription = unwrapNullableField(rawUser['position_description']);
      const positionDescriptionFileName = unwrapNullableField(rawUser['position_description_filename']);
      const department = unwrapNullableField(rawUser['department']);
      const unit = unwrapNullableField(rawUser['unit']);

      const trainerContactSource = rawUser['trainer_contact'];
      const trainerContact =
        typeof trainerContactSource === 'boolean'
          ? trainerContactSource
          : Boolean(
            trainerContactSource &&
            typeof trainerContactSource === 'object' &&
            'Bool' in (trainerContactSource as Record<string, unknown>) &&
            (trainerContactSource as Record<string, unknown>).Bool
          );

      const isHrManager = normalizeBooleanField(rawUser['is_hr_manager'] ?? rawUser['is_hrmanager']);

      // Compute status
      const isAnonymized = normalizeBooleanField(rawUser['is_anonymized']);
      const isActive = normalizeBooleanField(rawUser['is_active']);
      const systemAccessEnabled = normalizeBooleanField(rawUser['system_access_enabled']);
      let computedStatus = 'Active';
      if (isAnonymized) {
        computedStatus = 'Anonymized';
      } else if (isActive === true || systemAccessEnabled === true) {
        computedStatus = employmentStatus === 'Inactive' ? 'Inactive' : 'Active';
      } else {
        if (employmentStatus === 'Terminated') computedStatus = 'Terminated';
        else if (employmentStatus === 'Inactive') computedStatus = 'Inactive';
        else if (employmentStatus === 'Active') computedStatus = 'Active';
        else if (isActive === false) computedStatus = 'Inactive';
      }

      const mappedEducations = mapEducationRecords(apiEducations);
      const mappedExperiences = mapExperienceRecords(apiExperiences);

      const normalizeTotalExperience = (value: any): number | null => {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
          const n = Number(value);
          return Number.isFinite(n) ? n : null;
        }
        if (value && typeof value === 'object') {
          const maybe = (value as any).String ?? (value as any).Value ?? (value as any).value;
          if (maybe != null) {
            const n = Number(maybe);
            return Number.isFinite(n) ? n : null;
          }
        }
        return null;
      };

      const totalExperience = apiExperiences
        .map(exp => normalizeTotalExperience((exp as any).total_years_experience))
        .find(value => value !== null);

      const employeeDetails: EmployeeDetails = {
        id: apiUser.user_uuid || String(apiUser.id),
        user_uuid: apiUser.user_uuid,
        name,
        empId: employeeId || '',
        email: emailAddress || '',
        phone: phoneNumber,
        jobTitle: jobTitleApi || '',
        jobTitleId: jobTitleIdApi,
        grade: jobGradeApi || '',
        manager: manager || '-',
        hrPartner: hrPartner || '-',
        location: locationText || '',
        unit: unit ?? undefined,
        department: department || '',
        status: computedStatus,
        avatarColor: '#4B45E5',
        avatarInitial: name.charAt(0).toUpperCase() || '?',
        isHrManager,
        profile_pic_url: profilePicUrl,
        profilePicture: profilePicUrl,
        primary_org_unit_id: (rawUser as any).primary_org_unit_id ? String((rawUser as any).primary_org_unit_id) : undefined,
        primaryOrgUnit: (rawUser as any).primary_org_unit_id ? String((rawUser as any).primary_org_unit_id) : undefined,
        org_path: apiUser.org_path || (fullUserData as any)?.org_path || undefined,
        firstName,
        lastName,
        gender,
        race: race ?? undefined,
        dateOfBirth: dobISO ? formatDateOnly(dobISO) : null,
        employee_id: employeeId ?? undefined,
        educations: mappedEducations,
        experiences: mappedExperiences,
        totalYearsOfExperience: typeof totalExperience === 'number' ? totalExperience : null,
        hireDateISO,
        hireDate: formatDateOnly(hireDateISO),
        startDateISO,
        startDate: formatDateOnly(startDateISO),
        positionDateISO,
        positionDate: formatDateOnly(positionDateISO),
        standardHours: standardHours ?? undefined,
        eeocCategory: eeocCategory ?? undefined,
        entity: entity ?? undefined,
        division: division ?? undefined,
        secondaryManager: secondaryManager ?? undefined,
        employmentType: employmentType ?? undefined,
        jobCode: jobCode ?? undefined,
        reportingToTitle: reportingToTitle ?? undefined,
        workRole: workRole ?? undefined,
        trainerContact,
        branch: locationText ?? undefined,
        phoneExt: phoneExt ?? undefined,
        positionDescription: positionDescription ?? null,
        positionDescriptionFileName: positionDescriptionFileName ?? null,
        basePay: (rawUser['base_pay'] as number | null) ?? null,
        bonusAmount: (rawUser['bonus_amount'] as number | null) ?? null,
        bonusPercent: (rawUser['bonus_percent'] as number | null) ?? null,
        equity: (rawUser['equity'] as number | null) ?? null,
        payRate: (rawUser['pay_rate'] as number | null) ?? null,
        lastUpdated: new Date(),
      };

      set({ selectedEmployee: employeeDetails, isLoading: false });
    } catch (error) {
      console.error('Failed to load profile:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load profile',
        isLoading: false,
      });
    }
  },

  updateEmployee: async (updates: Partial<EmployeeDetails>) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee) return;

    set({ isLoading: true, error: null });

    try {
      const userUpdateData: Partial<CreateUserRequest> = {};
      const hasField = (field: keyof EmployeeDetails) => field in updates;

      if (hasField('firstName')) userUpdateData.first_name = updates.firstName || selectedEmployee.name?.split(' ')[0] || '';
      if (hasField('lastName')) userUpdateData.last_name = updates.lastName || selectedEmployee.name?.split(' ').slice(1).join(' ') || '';
      if (hasField('email')) userUpdateData.email = updates.email!;
      if (hasField('empId') || hasField('employee_id')) userUpdateData.employee_id = updates.empId || updates.employee_id;
      if (hasField('phone')) userUpdateData.phone = updates.phone ?? undefined;
      if (hasField('gender')) userUpdateData.gender = updates.gender ?? undefined;
      if (hasField('race')) userUpdateData.race = updates.race ?? undefined;
      if (hasField('profilePicture') || hasField('profile_pic_url')) {
        userUpdateData.profile_pic_url = updates.profilePicture ?? updates.profile_pic_url ?? undefined;
      }
      if (hasField('dateOfBirth')) {
        if (updates.dateOfBirth && updates.dateOfBirth !== 'null' && updates.dateOfBirth !== '') {
          userUpdateData.dob = updates.dateOfBirth;
        }
      }
      if (hasField('jobTitleId')) {
        const jobTitleIdVal = (updates as any).jobTitleId ?? (updates as any).job_title_id;
        if (jobTitleIdVal) userUpdateData.job_title_id = jobTitleIdVal;
      } else if (hasField('jobTitle')) {
        userUpdateData.job_title = updates.jobTitle;
      }
      if (hasField('grade')) userUpdateData.job_grade = updates.grade;
      if (hasField('status')) userUpdateData.employment_status = updates.status;
      if (hasField('employmentType')) userUpdateData.employment_type = updates.employmentType;
      if (hasField('hireDateISO')) userUpdateData.hire_date = formatDateForAPI(updates.hireDateISO);
      if (hasField('startDateISO')) userUpdateData.start_date = formatDateForAPI(updates.startDateISO);
      if (hasField('positionDateISO')) userUpdateData.position_date = formatDateForAPI(updates.positionDateISO);
      if (hasField('eeocCategory')) userUpdateData.eeoc_category = updates.eeocCategory ?? undefined;
      if (hasField('standardHours')) userUpdateData.standard_hr_per_week = updates.standardHours ?? undefined;
      if (hasField('entity')) userUpdateData.entity = updates.entity ?? undefined;
      if (hasField('division')) userUpdateData.division = updates.division ?? undefined;
      if (hasField('primaryOrgUnit')) userUpdateData.primary_org_unit = updates.primaryOrgUnit ?? undefined;
      if (hasField('manager')) {
        const value = updates.manager;
        if (value && value !== '-') {
          userUpdateData.manager = value.toString();
        }
      }
      if (hasField('secondaryManager')) {
        const value = updates.secondaryManager;
        if (value && value !== '-') {
          userUpdateData.secondary_manager = value.toString();
        }
      }
      if (hasField('hrPartner')) {
        const value = updates.hrPartner;
        if (value && value !== '-') {
          userUpdateData.hr_partner = value.toString();
        }
      }
      if (hasField('location')) {
        const locVal = updates.location;
        if (locVal) {
          const locNum = Number(locVal);
          if (!Number.isNaN(locNum)) {
            userUpdateData.location_id = locNum;
          }
        }
      }
      if (hasField('trainerContact')) userUpdateData.trainer_contact = updates.trainerContact ?? undefined;
      if (hasField('jobCode')) userUpdateData.job_code = updates.jobCode ?? undefined;
      if (hasField('positionDescription')) userUpdateData.position_description = updates.positionDescription || '';
      if (hasField('positionDescriptionFileName')) userUpdateData.position_description_filename = updates.positionDescriptionFileName || '';

      if (hasField('workRole') || (updates as any).work_role !== undefined) {
        const workRoleValue = (updates as any).work_role ?? updates.workRole;
        if (workRoleValue) userUpdateData.work_role = workRoleValue;
      }

      if (hasField('educations')) {
        userUpdateData.education = updates.educations
          ?.filter(edu => edu.university || edu.education || edu.degree || edu.year)
          .map(edu => ({
            school_name: edu.university ?? '',
            level: edu.degree ?? '',
            field_of_study: edu.education ?? '',
            year: edu.year ? parseInt(edu.year, 10) || 0 : 0,
          }));
      }

      // Remove undefined values
      Object.keys(userUpdateData).forEach(key => {
        if (userUpdateData[key as keyof CreateUserRequest] === undefined) {
          delete userUpdateData[key as keyof CreateUserRequest];
        }
      });

      if (Object.keys(userUpdateData).length === 0) {
        set({ isLoading: false });
        return;
      }

      const userIdToUpdate = selectedEmployee.user_uuid || selectedEmployee.id;
      await userService.updateUser(userIdToUpdate, userUpdateData);

      // Refresh from backend
      await get().refreshEmployeeDetails();

      // Sync updated profile fields to auth context (TopBar) without full /me refetch
      if (userUpdateData.first_name || userUpdateData.last_name || userUpdateData.email || userUpdateData.profile_pic_url) {
        try {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const parsed = JSON.parse(storedUser);
            if (userUpdateData.first_name !== undefined) parsed.first_name = userUpdateData.first_name;
            if (userUpdateData.last_name !== undefined) parsed.last_name = userUpdateData.last_name;
            if (userUpdateData.email !== undefined) parsed.email = userUpdateData.email;
            if (userUpdateData.profile_pic_url !== undefined) parsed.profile_pic_url = userUpdateData.profile_pic_url;
            localStorage.setItem('user', JSON.stringify(parsed));
            // Lightweight event — auth context patches user state without /me call
            window.dispatchEvent(new CustomEvent('auth-user-patched', { detail: parsed }));
          }
        } catch (e) {
          console.warn('Failed to sync profile to auth:', e);
        }
      }

      set({ isLoading: false });
    } catch (error) {
      console.error('Failed to update employee:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update employee',
        isLoading: false,
      });
    }
  },

  updatePersonalInfo: async (updates: Partial<EmployeeDetails>) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee) return;

    set({ isLoading: true, error: null });

    try {
      const userUpdateData: Partial<CreateUserRequest> = {};
      const hasField = (field: keyof EmployeeDetails) => field in updates;

      if (hasField('firstName')) userUpdateData.first_name = updates.firstName || '';
      if (hasField('lastName')) userUpdateData.last_name = updates.lastName || '';
      if (hasField('email')) userUpdateData.email = updates.email!;
      if (hasField('empId') || hasField('employee_id')) userUpdateData.employee_id = updates.empId || updates.employee_id;
      if (hasField('phone')) userUpdateData.phone = updates.phone ?? undefined;
      if (hasField('gender')) userUpdateData.gender = updates.gender ?? undefined;
      if (hasField('race')) userUpdateData.race = updates.race ?? undefined;
      if (hasField('profilePicture') || hasField('profile_pic_url')) {
        userUpdateData.profile_pic_url = updates.profilePicture ?? updates.profile_pic_url ?? undefined;
      }
      if (hasField('dateOfBirth')) {
        if (updates.dateOfBirth && updates.dateOfBirth !== 'null' && updates.dateOfBirth !== '') {
          userUpdateData.dob = updates.dateOfBirth;
        }
      }
      if (hasField('jobTitle')) userUpdateData.job_title = updates.jobTitle;
      if (hasField('grade')) userUpdateData.job_grade = updates.grade;
      if (hasField('status')) userUpdateData.employment_status = updates.status;
      if (hasField('employmentType')) userUpdateData.employment_type = updates.employmentType;
      if (hasField('reportingToTitle')) userUpdateData.reporting_to_title = updates.reportingToTitle ?? undefined;
      if (hasField('workRole')) userUpdateData.work_role = updates.workRole ?? undefined;
      if (hasField('trainerContact')) userUpdateData.trainer_contact = updates.trainerContact ?? undefined;
      if (hasField('hireDateISO')) userUpdateData.hire_date = formatDateForAPI(updates.hireDateISO);
      if (hasField('startDateISO')) userUpdateData.start_date = formatDateForAPI(updates.startDateISO);
      if (hasField('positionDateISO')) userUpdateData.position_date = formatDateForAPI(updates.positionDateISO);
      if (hasField('eeocCategory')) userUpdateData.eeoc_category = updates.eeocCategory ?? undefined;
      if (hasField('standardHours')) userUpdateData.standard_hr_per_week = updates.standardHours ?? undefined;
      if (hasField('jobCode')) userUpdateData.job_code = updates.jobCode ?? undefined;
      if (hasField('positionDescription')) userUpdateData.position_description = updates.positionDescription || '';
      if (hasField('positionDescriptionFileName')) userUpdateData.position_description_filename = updates.positionDescriptionFileName || '';

      if (hasField('educations')) {
        userUpdateData.education = (updates.educations)
          ?.filter(edu => edu.university || edu.education || edu.degree || edu.year)
          .map(edu => ({
            school_name: edu.university ?? '',
            level: edu.degree ?? '',
            field_of_study: edu.education ?? '',
            year: edu.year ? parseInt(edu.year, 10) || 0 : 0,
          }));
      }
      if (hasField('experiences')) {
        userUpdateData.experience = (updates.experiences)
          ?.filter(exp => exp.organisation || exp.jobTitle || exp.startDate || exp.endDate)
          .map(exp => ({
            company: exp.organisation ?? '',
            position: exp.jobTitle ?? '',
            total_years_experience: (selectedEmployee.totalYearsOfExperience === null || selectedEmployee.totalYearsOfExperience === undefined)
              ? null
              : Number(selectedEmployee.totalYearsOfExperience),
            start_date: exp.startDateISO ? formatDateForAPI(exp.startDateISO) || '' : '',
            end_date: exp.endDateISO ? formatDateForAPI(exp.endDateISO) || '' : '',
            description: '',
          }));
      }

      // Remove undefined values
      Object.keys(userUpdateData).forEach(key => {
        if (userUpdateData[key as keyof CreateUserRequest] === undefined) {
          delete userUpdateData[key as keyof CreateUserRequest];
        }
      });

      if (Object.keys(userUpdateData).length === 0) {
        set({ isLoading: false });
        return;
      }

      const userIdToUpdate = selectedEmployee.user_uuid || selectedEmployee.id;
      await userService.updateUser(userIdToUpdate, userUpdateData);

      const updatedEmployee = { ...selectedEmployee, ...updates, lastUpdated: new Date() };
      set({ selectedEmployee: updatedEmployee, isLoading: false });
    } catch (error) {
      console.error('Failed to update personal info:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update personal info',
        isLoading: false,
      });
    }
  },

  refreshEmployeeDetails: async () => {
    const { selectedEmployee } = get();
    if (!selectedEmployee?.user_uuid) return;
    await get().loadProfile(selectedEmployee.user_uuid);
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),
  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null }),

  addEducation: async (education: any) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee) return;
    const newEducation = { ...education, id: generateId() };
    const updatedEducations = [...(selectedEmployee.educations || []), newEducation];
    set({
      selectedEmployee: { ...selectedEmployee, educations: updatedEducations, lastUpdated: new Date() },
    });
  },

  updateEducation: async (educationId: string, updates: any) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee?.educations) return;
    const updatedEducations = selectedEmployee.educations.map(edu =>
      edu.id === educationId ? { ...edu, ...updates } : edu
    );
    set({
      selectedEmployee: { ...selectedEmployee, educations: updatedEducations, lastUpdated: new Date() },
    });
    await get().updatePersonalInfo({ educations: updatedEducations });
  },

  removeEducation: async (educationId: string) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee?.educations) return;
    const updatedEducations = selectedEmployee.educations.filter(edu => edu.id !== educationId);
    set({
      selectedEmployee: { ...selectedEmployee, educations: updatedEducations, lastUpdated: new Date() },
    });
  },

  addExperience: async (experience: any) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee) return;
    const newExperience = { ...experience, id: generateId() };
    const updatedExperiences = [...(selectedEmployee.experiences || []), newExperience];
    set({
      selectedEmployee: { ...selectedEmployee, experiences: updatedExperiences, lastUpdated: new Date() },
    });
  },

  updateExperience: async (experienceId: string, updates: any) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee?.experiences) return;
    const updatedExperiences = selectedEmployee.experiences.map(exp =>
      exp.id === experienceId ? { ...exp, ...updates } : exp
    );
    set({
      selectedEmployee: { ...selectedEmployee, experiences: updatedExperiences, lastUpdated: new Date() },
    });
    await get().updatePersonalInfo({ experiences: updatedExperiences });
  },

  removeExperience: async (experienceId: string) => {
    const { selectedEmployee } = get();
    if (!selectedEmployee?.experiences) return;
    const updatedExperiences = selectedEmployee.experiences.filter(exp => exp.id !== experienceId);
    set({
      selectedEmployee: { ...selectedEmployee, experiences: updatedExperiences, lastUpdated: new Date() },
    });
  },
}));
